import {
  RequestParameters,
  RequestTransformFunction,
  ResourceType,
} from 'mapbox-gl';
import { useCallback } from 'react';
import { getAuthenticationHeaders } from 'api';
import axios, { AxiosResponse } from 'axios';
import { isProductionEnvironment } from 'shared/envUtils';
import { ENV } from 'shared/ENV';

const DEV_ONLY_API_BASE_URL = 'http://localhost:8000/api/v1/';

const isBackendUrl = (url: string): boolean => {
  if (url.startsWith(ENV.VITE_BASE_API_URL)) {
    return true;
  }

  // In local dev, the backend will report it's running on localhost:8000, but the VITE env in the frontend
  // may be a network IP address. So we need to check for both.
  if (!isProductionEnvironment() && url.startsWith(DEV_ONLY_API_BASE_URL)) {
    return true;
  }
  return false;
};

export const useAuthenticatedTileRequestTransformer =
  (): RequestTransformFunction => {
    const addAuthHeadersToRequest = useCallback(
      (
        url: string,
        _resourceTypeEnum: ResourceType | unknown,
      ): RequestParameters => {
        if (!isBackendUrl(url)) {
          return { url };
        }
        const authHeaders = getAuthenticationHeaders();
        return {
          url,
          headers: authHeaders,
        };
      },
      [],
    );
    return addAuthHeadersToRequest;
  };

export const AuthenticatedTileAPI = {
  /**
   * Performs a HEAD request to the given URL. If the URL is a backend URL, adds auth headers.
   */
  head: (url: string): Promise<AxiosResponse> => {
    if (isBackendUrl(url)) {
      return axios.head(url, {
        headers: getAuthenticationHeaders(),
      });
    }
    return axios.head(url);
  },
  /**
   * Performs a GET request to the given URL. If the URL is a backend URL, it adds auth headers.
   */
  get: (url: string): Promise<AxiosResponse> => {
    if (isBackendUrl(url)) {
      return axios.get(url, {
        headers: getAuthenticationHeaders(),
      });
    }
    return axios.get(url);
  },
};
